// DEV-09 韌體管理（/devices/firmware）：範圍內每台設備本體的版本、可用更新、自動更新與維護時段（= 排程），多選套用 / 立即更新，
// 以及更新報表（期間內每一次更新的結果，可下載 CSV）。資料是 OTA 既有的 App API；什麼時候推哪一版由 release 與 dispatcher 決定。
// 是設備頁的「韌體」分頁（外框 DevicesLayout，VMS UX 評審 2026-10-13 P0-4）；?status=outdated 預先篩「有新版」（總覽韌體卡的連結）。
// 台數跟設備清單的「設備」同一個定義（設備本體，通道跟著 parent 不列）；總覽的韌體卡用同一個 useFleetFirmware。
import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../components/ConfirmDialog';
import { Dialog } from '../../components/Dialog';
import { useScope } from '../../lib/scope';
import { useDevices } from '../../lib/useDevices';
import { canManage, modelDisplay, type VmsDevice } from '../../lib/devices';
import { homeLabel } from '../devices/useHomeDetails';
import { fwApi, homeFirmware, type ScopeHome, type HomeFirmwareStatus } from '../../lib/planeApi';
import { batchSummary, retryFailed, runBatch, type BatchOutcome, type BatchRow } from '../devices/batch';
import { fleetSummary, fwState, reportCsv, reportRows, reportSummary, validWindow, type ReportRow } from './fleet';
import { stamp } from '../../lib/timefmt';

interface Row {
  key: string;
  home: ScopeHome;
  s: HomeFirmwareStatus;
  name: string;
  canEdit: boolean;
  /** 設備清單上的那台（機種欄跟清單同一個顯示函式，第六輪 B6-12） */
  dev?: VmsDevice;
}

const errCode = (e: unknown) => String((e as { code?: string }).code ?? (e as Error).message ?? e);

/** 目前範圍內每台設備本體的韌體狀態（韌體頁與總覽「韌體」卡共用）。 */
export function useFleetFirmware(refetch = true) {
  const all = useScope().visible;
  // 韌體（OTA）是家戶成員的事；只經分享進來的家戶沒有（SPEC #device-permissions-layers）
  const visible = useMemo(() => all.filter((h) => !h.external), [all]);
  const { tree } = useDevices();
  const qs = useQueries({
    queries: visible.map((h) => ({
      queryKey: ['home-firmware', h.home_id],
      queryFn: () => homeFirmware(h.endpoints.api, h.home_id),
      retry: false,
      refetchInterval: refetch ? 30_000 : undefined,
      staleTime: refetch ? 0 : 5 * 60_000,
    })),
  });
  const rows = useMemo(() => {
    const out: Row[] = [];
    const all = tree.flatMap((d) => [d, ...d.children]); // tree 只有頂層，通道在 children 裡
    qs.forEach((q, i) => {
      const home = visible[i];
      for (const s of q.data ?? []) {
        const dev = all.find((d) => d.homeId === home.home_id && d.aid === s.device_ref);
        if (dev?.parentAid) continue; // 通道跟著 parent
        out.push({ key: home.home_id + '/' + s.device_ref, home, s, dev, name: dev?.name ?? s.device_ref, canEdit: !!dev && canManage(dev) && !s.error }); // OTA 是管理動作（can_manage）；讀不到狀態的不給改（不盲寫）
      }
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs.map((q) => q.dataUpdatedAt).join(','), visible, tree]);
  return { rows, qs, visible, loading: qs.some((q) => q.isLoading), known: qs.some((q) => q.isSuccess) };
}

export function FirmwarePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { rows: allRows, qs, visible, loading } = useFleetFirmware();
  const [sp, setSp] = useSearchParams();
  const outdated = sp.get('status') === 'outdated';
  const rows = useMemo(() => (outdated ? allRows.filter((r) => fwState(r.s) === 'available') : allRows), [allRows, outdated]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [batch, setBatch] = useState<'schedule' | 'update' | null>(null);
  const sum = fleetSummary(allRows.map((r) => r.s));
  const editable = rows.filter((r) => r.canEdit);
  const pickedRows = editable.filter((r) => picked.has(r.key));
  const toggle = (k: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['home-firmware'] });

  return (
    <div className="page" data-testid="fw-page">
      <p className="muted">{t('fwm.help')}</p>
      <div className="kpis">
        {(['latest', 'available', 'updating', 'unknown'] as const).map((k) => (
          <div className="card kpi" key={k} data-fw-kpi={k}>
            <div className="muted">{t(`fwm.st_${k}`)}</div>
            <div className="value">{loading ? '—' : sum[k]}</div>
          </div>
        ))}
      </div>
      {qs.map((q, i) =>
        q.isError ? (
          <p key={visible[i].home_id} className="muted small">
            {t('fwm.home_failed', { home: homeLabel(visible[i]) })}
          </p>
        ) : null,
      )}
      <div className="toolbar">
        <span className="muted">{t('fwm.count', { count: rows.length })}</span>
        <label className="check small">
          <input
            type="checkbox"
            checked={outdated}
            onChange={(e) => {
              const n = new URLSearchParams(sp);
              if (e.target.checked) n.set('status', 'outdated');
              else n.delete('status');
              setSp(n, { replace: true });
            }}
            data-testid="fw-only-outdated"
          />{' '}
          {t('fwm.only_outdated')}
        </label>
        {pickedRows.length > 0 && (
          <>
            <button className="btn" onClick={() => setBatch('schedule')} data-testid="fw-batch-schedule">
              {t('fwm.batch_schedule', { n: pickedRows.length })}
            </button>
            <button className="btn" onClick={() => setBatch('update')} data-testid="fw-batch-update">
              {t('fwm.batch_update', { n: pickedRows.length })}
            </button>
          </>
        )}
      </div>
      {/* 怎麼生效寫清楚（第五輪 B5-16：每列的〔儲存〕拿掉後，看不出改了有沒有存） */}
      {editable.length > 0 && (
        <p className="muted small" data-testid="fw-how-saved">
          {t('fwm.how_saved')}
        </p>
      )}
      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                {editable.length > 0 && (
                  <input
                    type="checkbox"
                    aria-label={t('batch.pick_all')}
                    checked={pickedRows.length === editable.length}
                    onChange={(e) => setPicked(e.target.checked ? new Set(editable.map((r) => r.key)) : new Set())}
                  />
                )}
              </th>
              <th>{t('devices.col_name')}</th>
              <th>{t('devices.col_home')}</th>
              <th>{t('fwm.col_model')}</th>
              <th>{t('devices.col_fw')}</th>
              <th>{t('fwm.col_state')}</th>
              <th>{t('ddev.fw_auto')}</th>
              <th>{t('fwm.col_window')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <FwRow key={r.key} r={r} picked={picked.has(r.key)} onPick={() => toggle(r.key)} onSaved={refresh} />
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="muted">
                  {t('fwm.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Report rows={rows} />
      {batch && (
        <FleetBatch
          mode={batch}
          rows={pickedRows}
          close={() => {
            setBatch(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/** 一列：自動更新與維護時段各自儲存（owner 才能改）。 */
function FwRow({ r, picked, onPick, onSaved }: { r: Row; picked: boolean; onPick(): void; onSaved(): void }) {
  const { t } = useTranslation();
  const { s, home } = r;
  const st = fwState(s);
  const saved = s.settings?.maintenance_window ?? '';
  const [win, setWin] = useState(saved);
  const [seen, setSeen] = useState(saved);
  const [busy, setBusy] = useState<'auto' | 'win' | null>(null);
  const [autoOpt, setAutoOpt] = useState<boolean | null>(null); // 開關先跟手，存失敗再彈回
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  if (seen !== saved) {
    // 伺服器上的值變了（別人改 / 批次套用）：沒在編輯就跟上
    setSeen(saved);
    if (win === seen) setWin(saved);
  }
  const save = async (what: 'auto' | 'win', body: { auto_update?: boolean; maintenance_window?: string }) => {
    setBusy(what);
    setMsg(null);
    try {
      await fwApi.settings(home.endpoints.api, home.home_id, s.device_ref, body);
      setMsg({ ok: true, text: t('fwm.saved') });
      onSaved();
    } catch (e) {
      setMsg({ ok: false, text: t(`fwm.err_${errCode(e)}`, { defaultValue: errCode(e) }) });
      setAutoOpt(null);
    }
    setBusy(null);
  };
  const serverAuto = !!s.settings?.auto_update;
  if (autoOpt !== null && autoOpt === serverAuto) setAutoOpt(null);
  const auto = autoOpt ?? serverAuto;
  return (
    <tr data-fw-row={s.device_ref} data-state={st}>
      <td>{r.canEdit && <input type="checkbox" checked={picked} onChange={onPick} aria-label={t('batch.pick', { name: r.name })} />}</td>
      <td>
        <Link to={`/devices/${encodeURIComponent(home.home_id)}/${encodeURIComponent(s.device_ref)}`}>{r.name}</Link>
        {s.online === false && <span className="chip">{t('devices.offline')}</span>}
      </td>
      <td>{homeLabel(home)}</td>
      {/* 跟設備清單同一個欄名「機種」、同一個值（model_name，例「Kinio Node（BL602）」；第六輪 B6-12） */}
      <td className="small">{modelDisplay(t, { model: r.dev?.model || s.model || '', modelName: r.dev?.modelName, modelSource: r.dev?.modelSource })}</td>
      <td className="mono">{s.current_version || '—'}</td>
      <td>
        {st === 'updating' ? (
          <span className="badge">{t('fwm.updating', { v: s.job?.version ?? '', p: s.job?.progress ?? 0 })}</span>
        ) : st === 'available' ? (
          <span className="badge badge-warn">
            {t('fwm.available', { v: s.release?.version ?? '' })}
            {s.release?.critical ? ' · ' + t('fwm.critical') : ''}
          </span>
        ) : (
          <span className="muted">{st === 'unknown' && s.online === false ? t('fwm.st_unknown_offline') : t(`fwm.st_${st}`)}</span>
        )}
      </td>
      <td>
        {s.error ? (
          '—'
        ) : (
          <label className="row-inline">
            <input
              type="checkbox"
              role="switch"
              checked={auto}
              disabled={!r.canEdit || busy !== null}
              aria-label={t('fwm.auto_of', { name: r.name })}
              onChange={(e) => {
                setAutoOpt(e.target.checked);
                void save('auto', { auto_update: e.target.checked });
              }}
            />{' '}
            {auto ? t('common.on') : t('common.off')}
          </label>
        )}
      </td>
      <td>
        {s.error ? (
          '—'
        ) : r.canEdit ? (
          <span className="row-inline">
            <input
              className="input mono"
              style={{ width: 120 }}
              value={win}
              placeholder="02:00-05:00"
              aria-label={t('fwm.window_of', { name: r.name })}
              aria-invalid={win !== '' && !validWindow(win)}
              onChange={(e) => setWin(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && win !== saved && validWindow(win) && busy === null && void save('win', { maintenance_window: win.trim() })}
            />{' '}
            {/* 改了才出現〔儲存〕（第四輪 R4-P1-8：原本每列常駐一顆，最後幾列又被浮動通知蓋住） */}
            {win !== saved && (
              <button className="btn btn-sm btn-primary" disabled={busy !== null || !validWindow(win)} onClick={() => void save('win', { maintenance_window: win.trim() })}>
                {t('common.save')}
              </button>
            )}
          </span>
        ) : (
          <span className="mono">{saved || '—'}</span>
        )}
        {msg && <span className={'small ' + (msg.ok ? 'muted' : 'error')}> {msg.text}</span>}
      </td>
    </tr>
  );
}

/** 多選：套用排程（自動更新 + 維護時段）或立即更新；逐台結果、失敗的可重試。 */
function FleetBatch({ mode, rows, close }: { mode: 'schedule' | 'update'; rows: Row[]; close(): void }) {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const [auto, setAuto] = useState<'keep' | 'on' | 'off'>('on');
  const [win, setWin] = useState('02:00-05:00');
  const [res, setRes] = useState<BatchRow<Row>[] | null>(null);
  const [busy, setBusy] = useState(false);
  const winOk = win.trim() === '' || validWindow(win);
  const nothing = mode === 'schedule' && auto === 'keep' && win.trim() === '';
  const act = async (r: Row): Promise<BatchOutcome> => {
    const { api } = r.home.endpoints;
    if (mode === 'schedule') {
      const body: { auto_update?: boolean; maintenance_window?: string } = {};
      if (auto !== 'keep') body.auto_update = auto === 'on';
      if (win.trim()) body.maintenance_window = win.trim();
      await fwApi.settings(api, r.home.home_id, r.s.device_ref, body);
      return { state: 'ok' };
    }
    if (fwState(r.s) === 'updating') return { state: 'skipped', detail: t('fwm.skip_updating') };
    if (!r.s.update_available) return { state: 'skipped', detail: t('ddev.fw_latest') };
    const out = await fwApi.update(api, r.home.home_id, r.s.device_ref);
    if (out?.delivery === 'app') return { state: 'skipped', detail: t('ddev.fw_via_app') };
    if (out && out.started === false) throw Object.assign(new Error(out.reason ?? 'not_started'), { code: out.reason ?? 'not_started' });
    return { state: 'ok', detail: `→ ${out?.version ?? r.s.release?.version ?? ''}` };
  };
  const run = async (from: BatchRow<Row>[]) => {
    if (mode === 'update' && !res && !(await ask({ title: t('batch.fw_confirm', { count: from.length }), action: t('common.action_update_fw') }))) return;
    setBusy(true);
    setRes([...from]);
    await runBatch(from, act, (x) => setRes([...x]));
    setBusy(false);
  };
  const sum = res ? batchSummary(res) : null;
  return (
    <Dialog open title={t(`fwm.batch_${mode}`, { n: rows.length })} onClose={close} wide>
      <div className="form" data-testid="fw-batch">
        {mode === 'schedule' ? (
          <div className="toolbar">
            <label className="field">
              <span>{t('ddev.fw_auto')}</span>
              <select className="input" value={auto} onChange={(e) => setAuto(e.target.value as typeof auto)} disabled={busy} aria-label={t('ddev.fw_auto')}>
                <option value="on">{t('common.on')}</option>
                <option value="off">{t('common.off')}</option>
                <option value="keep">{t('fwm.keep')}</option>
              </select>
            </label>
            <label className="field">
              <span>{t('fwm.col_window')}</span>
              <input
                className="input mono"
                value={win}
                onChange={(e) => setWin(e.target.value)}
                disabled={busy}
                aria-invalid={!winOk}
                aria-label={t('fwm.col_window')}
                placeholder={t('fwm.keep')}
              />
            </label>
          </div>
        ) : null}
        <p className="muted small">{t(`fwm.batch_${mode}_help`)}</p>
        <div className="table-wrap" style={{ maxHeight: 320, overflow: 'auto' }}>
          <table className="grid">
            <tbody>
              {(res ?? rows.map((r) => ({ item: r, state: 'pending' as const, detail: undefined }))).map((x) => (
                <tr key={x.item.key} data-batch-row={x.item.s.device_ref} data-state={res ? x.state : 'idle'}>
                  <td>{x.item.name}</td>
                  <td className="mono">{x.item.s.current_version || '—'}</td>
                  <td>
                    {!res ? (
                      '—'
                    ) : x.state === 'failed' ? (
                      <span className="error">{t(`fwm.err_${x.detail}`, { defaultValue: t('common.op_failed', { code: x.detail }) })}</span>
                    ) : x.state === 'ok' ? (
                      `✓ ${x.detail ?? ''}`
                    ) : x.state === 'skipped' ? (
                      x.detail
                    ) : (
                      t(`batch.st_${x.state}`)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sum && (
          <p className="small" data-testid="fw-batch-summary">
            {t('batch.summary', { ok: sum.ok, skipped: sum.skipped, failed: sum.failed, left: sum.pending + sum.running })}
          </p>
        )}
        <div className="toolbar">
          {!res && (
            <button
              className="btn btn-primary"
              disabled={busy || !winOk || nothing}
              onClick={() => void run(rows.map((r) => ({ item: r, state: 'pending' as const })))}
            >
              {t('batch.run', { n: rows.length })}
            </button>
          )}
          {res && !busy && sum && sum.failed > 0 && (
            <button className="btn" onClick={() => void run(retryFailed(res))}>
              {t('fwm.retry', { n: sum.failed })}
            </button>
          )}
          <span className="spacer" />
          <button className="btn" onClick={close}>
            {t('common.close')}
          </button>
        </div>
      </div>
      {dialog}
    </Dialog>
  );
}

const PERIODS = [1, 7, 30] as const;

/** 更新報表：期間內每台的更新歷史。按了才讀（一台一個請求，同時最多 4 個）。 */
function Report({ rows }: { rows: Row[] }) {
  const { t } = useTranslation();
  const [days, setDays] = useState<(typeof PERIODS)[number]>(7);
  const [data, setData] = useState<{ rows: ReportRow[]; failed: number; days: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const build = async () => {
    const mine = ++seq.current;
    setBusy(true);
    const targets = rows.filter((r) => !r.s.error);
    const items: { home: string; device: string; aid: string; history: Awaited<ReturnType<typeof fwApi.history>> }[] = [];
    let failed = 0;
    let next = 0;
    const worker = async () => {
      for (;;) {
        const r = targets[next++];
        if (!r) return;
        try {
          items.push({ home: homeLabel(r.home), device: r.name, aid: r.s.device_ref, history: await fwApi.history(r.home.endpoints.api, r.home.home_id, r.s.device_ref) });
        } catch {
          failed++;
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, Math.max(1, targets.length)) }, worker));
    if (mine !== seq.current) return;
    setData({ rows: reportRows(items, Date.now() - days * 86_400_000), failed, days });
    setBusy(false);
  };
  const download = () => {
    if (!data) return;
    const header = ['at', 'home', 'device', 'aid', 'from', 'to', 'status', 'reason', 'trigger', 'finished'].map((k) => t(`fwm.csv_${k}`));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([reportCsv(data.rows, header)], { type: 'text/csv;charset=utf-8' }));
    a.download = `firmware-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
  };
  const sum = data ? reportSummary(data.rows) : null;
  return (
    <section className="card" data-testid="fw-report" style={{ marginTop: 16 }}>
      <h2>{t('fwm.report')}</h2>
      <div className="toolbar" style={{ alignItems: 'flex-end' }}>
        <label className="field">
          <span>{t('fwm.period')}</span>
          <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value) as typeof days)} aria-label={t('fwm.period')}>
            {PERIODS.map((d) => (
              <option key={d} value={d}>
                {t(`fwm.period_${d}`)}
              </option>
            ))}
          </select>
        </label>
        <button className="btn btn-primary" disabled={busy || rows.length === 0} onClick={() => void build()}>
          {busy ? t('common.loading') : t('fwm.report_build')}
        </button>
        {data && data.rows.length > 0 && (
          <button className="btn" onClick={download}>
            {t('fwm.report_csv')}
          </button>
        )}
      </div>
      {data && sum && (
        <>
          <p data-testid="fw-report-summary">
            {t('fwm.report_summary', { days: data.days, success: sum.success, failed: sum.failed, other: sum.other })}
            {data.failed > 0 && <span className="error"> {t('fwm.report_unread', { count: data.failed })}</span>}
          </p>
          {data.rows.length === 0 ? (
            <p className="muted">{t('fwm.report_empty')}</p>
          ) : (
            <div className="table-wrap" style={{ maxHeight: 420, overflow: 'auto' }}>
              <table className="grid">
                <thead>
                  <tr>
                    {(['at', 'home', 'device', 'to', 'status', 'trigger'] as const).map((k) => (
                      <th key={k}>{t(`fwm.csv_${k}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.aid + r.at} data-report-row={r.aid} data-result={r.result}>
                      <td className="small">{stamp(r.at)}</td>
                      <td>{r.home}</td>
                      <td>{r.device}</td>
                      <td className="mono">
                        {r.from || '?'} → {r.to}
                      </td>
                      <td>
                        <span className={r.result === 'failed' ? 'error' : undefined}>{t(`fwm.job_${r.status}`, { defaultValue: t('common.state_unknown') })}</span>
                        {r.reason && <span className="muted small"> · {t(`fwm.reason_${r.reason}`, { defaultValue: t('common.state_unknown') })}</span>}
                      </td>
                      <td>{t(`fwm.trigger_${r.trigger}`, { defaultValue: r.trigger })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
